import { WarningAltFilled, Information } from '@carbon/react/icons';
import { flagCategoryLabel } from 'components/entity-form/flag-categories';
import type { FlagSeverity } from 'types/flags';
import styles from './entity-flag-tag.module.scss';

interface Props {
  flagCategory?: string | null;
  flagRationale?: string | null;
  // Display severity of the flag. 'warning' → gated (yellow + warning icon; the
  // entity is part of a workspace or interpretation, so combining requires an
  // override). 'info' → informational (blue + info icon; flagged but part of
  // neither, so no override). null/undefined → unflagged: renders nothing.
  flagSeverity?: FlagSeverity | null;
  className?: string;
}

// A compact chip marking a consolidation-risk flagged entity. Renders nothing
// only when unflagged; every flag surfaces a chip — yellow + warning icon when
// gated (part of a workspace or interpretation), blue + info icon when
// informational (part of neither).
//
// A plain styled span rather than a Carbon <Tag> for one reason: Tag has no
// yellow `type`, so the gated fill would need a className override anyway. (Icon
// + a native tooltip both work on Tag — see well-workflow-status-badge and
// slot-exclusion-badge — so if the yellow fill is ever dropped, this could fold
// back onto Tag + a title-span.)
export const EntityFlagTag = ({
  flagCategory,
  flagRationale,
  flagSeverity,
  className,
}: Props) => {
  if (!flagCategory || !flagSeverity) return null;

  const gated = flagSeverity === 'warning';
  const label = flagCategoryLabel(flagCategory) || 'Flagged';
  const Icon = gated ? WarningAltFilled : Information;
  const rationale = flagRationale?.trim();
  // Two lines in the native tooltip: a heading, then the category and its reason.
  // `\n` renders as a line break in a `title`; aria-label stays a flat string.
  const detail = rationale ? `${label} — ${rationale}` : label;
  const tooltipText = `Consolidation-Risk Flag\n${detail}`;

  return (
    <span
      className={[styles.chip, gated ? styles.warning : styles.info, className]
        .filter(Boolean)
        .join(' ')}
      title={tooltipText}
      aria-label={`Consolidation-Risk Flag: ${detail}`}
    >
      <Icon size={16} className={styles.icon} />
      <span className={styles.label}>{label}</span>
    </span>
  );
};
